import { useState } from "react";
import { useMutation } from "@apollo/client";
import { DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import { UPDATE_SCHEDULED_NOTIFICATION } from "../../graphql/notificationQueries";
import { useToast } from "../Toast/toastHook";

const disabledTimeIfToday = (selectedDate) => {
  if (!selectedDate || !selectedDate.isSame(dayjs(), "day")) return {};
  const now = dayjs();
  return {
    disabledHours: () =>
      Array.from({ length: 24 }, (_, i) => i).filter((h) => h < now.hour()),
    disabledMinutes: (selectedHour) =>
      selectedHour === now.hour()
        ? Array.from({ length: 60 }, (_, i) => i).filter((m) => m <= now.minute())
        : [],
  };
};

const EditScheduledNotificationModal = ({ notification, onClose }) => {
  const toast = useToast(4000);
  const [title, setTitle] = useState(notification.title || "");
  const [body, setBody] = useState(notification.body || "");
  const [sendMode, setSendMode] = useState("schedule");
  const [date, setDate] = useState(dayjs(+notification.scheduledAt));
  const [time, setTime] = useState(dayjs(+notification.scheduledAt));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [updateScheduledNotification] = useMutation(UPDATE_SCHEDULED_NOTIFICATION, {
    refetchQueries: ["getSentNotifications"],
    onCompleted(result) {
      toast("success", result?.updateScheduledNotification || "Notification updated.");
      onClose();
    },
    onError(e) {
      toast("error", e?.message ?? "Something went wrong!");
      setSaving(false);
    },
  });

  const handleSave = () => {
    if (!title.trim() || !body.trim()) {
      setError("Title and description are required.");
      return;
    }
    let scheduledAt = null;
    if (sendMode === "schedule") {
      if (!date || !time) {
        setError("Pick a date and time to schedule this notification.");
        return;
      }
      const combined = date
        .hour(time.hour())
        .minute(time.minute())
        .second(0)
        .millisecond(0);
      if (combined.isBefore(dayjs())) {
        setError("Pick a time in the future.");
        return;
      }
      scheduledAt = combined.toISOString();
    }
    setError("");
    setSaving(true);
    updateScheduledNotification({
      variables: { id: notification._id, title, body, scheduledAt },
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 relative">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 p-1"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2 className="text-xl font-semibold text-gray-900 m-0 mb-4">
          Edit scheduled notification
        </h2>

        <label className="mb-2 block text-black text-sm font-medium">Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full mb-3 rounded-md border border-[#d9d9d9] py-2 px-3 outline-none focus:border-primary"
          placeholder="Enter title"
        />

        <label className="mb-2 block text-black text-sm font-medium">Description</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className="w-full mb-3 rounded-md resize-none border border-[#d9d9d9] py-2 px-3 outline-none focus:border-primary"
          placeholder="Enter description"
        />

        <label className="mb-2 block text-black text-sm font-medium">When to send</label>
        <div className="mb-3 flex gap-2">
          <button
            type="button"
            onClick={() => setSendMode("now")}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded border py-2 text-sm font-semibold ${
              sendMode === "now"
                ? "border-blue bg-blue/10 text-blue"
                : "border-[#e3e5e9] text-secondary"
            }`}
          >
            {sendMode === "now" && (
              <span className="inline-block w-2 h-2 rounded-full bg-blue" />
            )}
            Send now
          </button>
          <button
            type="button"
            onClick={() => setSendMode("schedule")}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded border py-2 text-sm font-semibold ${
              sendMode === "schedule"
                ? "border-blue bg-blue/10 text-blue"
                : "border-[#e3e5e9] text-secondary"
            }`}
          >
            {sendMode === "schedule" && (
              <span className="inline-block w-2 h-2 rounded-full bg-blue" />
            )}
            Schedule for later
          </button>
        </div>

        {sendMode === "schedule" && (
          <div className="mb-3 flex gap-3">
            <DatePicker
              allowClear={false}
              size="large"
              className="flex-1"
              placeholder="Select date"
              value={date}
              disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
              onChange={setDate}
              format="YYYY-MM-DD"
            />
            <TimePicker
              use12Hours
              size="large"
              className="flex-1"
              placeholder="Select time"
              allowClear={false}
              format="h:mm A"
              value={time}
              onChange={setTime}
              {...disabledTimeIfToday(date)}
            />
          </div>
        )}

        {error && <div className="py-1 text-xs text-red mb-2">{error}</div>}

        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-[#e3e5e9] px-4 py-2 text-sm font-semibold text-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditScheduledNotificationModal;
